import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Check, Loader2, Trash2 } from 'lucide-react';
import { gardensApi } from '../../services/api';
import { useDashboard } from '../../context/DashboardContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { useWizard } from '../../context/WizardContext';
import useGardenLoader from '../../hooks/useGardenLoader';
import DeleteGardenDialog from './DeleteGardenDialog';

/**
 * GardenSwitcherSheet — mobile bottom sheet for switching gardens.
 *
 * Replaces the GardenSwitcher dropdown on mobile (< 768px).
 * Swipe-to-reveal exposes a delete button per row; tap opens DeleteGardenDialog.
 *
 * Props:
 *   isOpen   boolean   — controls visibility
 *   onClose  () => void
 */
const GardenSwitcherSheet = ({ isOpen, onClose }) => {
  const { setActiveView, setMobileTab } = useDashboard();
  const { gardenId, resetSettings, setGardenId } = useGardenSettings();
  const { resetWizard } = useWizard();
  const { loadFromBackend } = useGardenLoader();

  const [gardens, setGardens] = useState([]);
  const [switching, setSwitching] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [wasOpen, setWasOpen] = useState(isOpen);

  // Swipe-to-reveal state
  const touchStartXRef = useRef(null);
  const [activeTouchId, setActiveTouchId] = useState(null);
  const [activeDragX, setActiveDragX] = useState(0);
  const [swipedId, setSwipedId] = useState(null);

  // Reset swipe/error state when the sheet closes, during render rather than in
  // an effect (avoids a synchronous setState-in-effect cascade).
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setSwipedId(null);
      setActiveTouchId(null);
      setSwitchError(null);
    }
  }

  // Load gardens when the sheet opens. The backend scopes the list to the
  // caller's session/anon cookie — no client-side user filter (#312, SEC-04 #525).
  useEffect(() => {
    if (!isOpen) return;
    gardensApi
      .getAll()
      .then((data) => setGardens(Array.isArray(data) ? data : []))
      .catch(() => setGardens([]));
  }, [isOpen]);

  // Block body scroll while sheet is open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleNewGarden = () => {
    onClose();
    resetWizard();
    resetSettings();
    setGardenId(null);
    localStorage.removeItem('gartenplaner_wizard_complete');
    localStorage.removeItem('gartenplaner_active_garden_id');
    localStorage.removeItem('gartenplaner_canvas');
    setActiveView('editor');
    setMobileTab('editor'); // mobile: mobileTab controls routing, activeView is ignored
  };

  const handleDelete = async ({ id }) => {
    await gardensApi.delete(id);
    const remaining = gardens.filter((g) => g.id !== id);
    setGardens(remaining);
    if (id === gardenId) {
      if (remaining.length > 0) {
        await loadFromBackend(remaining[0].id);
      } else {
        resetWizard();
        resetSettings();
        setGardenId(null);
        localStorage.removeItem('gartenplaner_wizard_complete');
        localStorage.removeItem('gartenplaner_active_garden_id');
        localStorage.removeItem('gartenplaner_canvas');
        setActiveView('editor');
        setMobileTab('editor');
      }
    }
    setDeleteTarget(null);
    onClose();
  };

  const handleSelect = async (garden) => {
    if (garden.id === gardenId) { onClose(); return; }
    setSwitching(garden.id);
    setSwitchError(null);
    try {
      await loadFromBackend(garden.id);
      onClose();
    } catch {
      setSwitchError('Der Garten konnte nicht geladen werden. Bitte versuche es erneut.');
    } finally {
      setSwitching(null);
    }
  };

  // Swipe-to-reveal helpers
  const getRowTranslateX = (id) => {
    if (activeTouchId === id) return activeDragX;
    if (swipedId === id) return -80;
    return 0;
  };

  const handleTouchStart = (id, e) => {
    touchStartXRef.current = e.touches[0].clientX;
    setActiveTouchId(id);
    // Start drag from the current locked position if already swiped
    setActiveDragX(swipedId === id ? -80 : 0);
    // Close any other open swipe row
    if (swipedId !== null && swipedId !== id) setSwipedId(null);
  };

  const handleTouchMove = (id, e) => {
    if (activeTouchId !== id || touchStartXRef.current === null) return;
    const baseX = swipedId === id ? -80 : 0;
    const delta = e.touches[0].clientX - touchStartXRef.current;
    setActiveDragX(Math.max(-80, Math.min(0, baseX + delta)));
  };

  const handleTouchEnd = (id) => {
    if (activeTouchId !== id) return;
    if (activeDragX < -60) {
      setSwipedId(id);
    } else {
      if (swipedId === id) setSwipedId(null);
    }
    setActiveTouchId(null);
    touchStartXRef.current = null;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Garten wechseln"
        className="relative bg-white rounded-t-2xl shadow-xl max-h-[80vh] flex flex-col"
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-9 h-1 bg-gray-300 rounded-full" />
        </div>

        {switchError && (
          <div className="mx-4 mb-2 px-3 py-2 text-xs text-red-600 bg-red-50 rounded-md border border-red-100 flex-shrink-0">
            {switchError}
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {gardens.length > 0 && (
            <ul aria-label="Gartenliste">
              {gardens.map((garden) => {
                const isActive = garden.id === gardenId;
                const isLoading = switching === garden.id;
                const translateX = getRowTranslateX(garden.id);
                const isRevealed = swipedId === garden.id;

                return (
                  <li key={garden.id} className="relative overflow-hidden">
                    {/* Delete button revealed by swipe */}
                    <div
                      className="absolute inset-y-0 right-0 w-20 flex items-center justify-center bg-gray-100"
                      aria-hidden={!isRevealed}
                    >
                      <button
                        type="button"
                        aria-label={`Garten löschen: ${garden.name}`}
                        tabIndex={isRevealed ? 0 : -1}
                        onClick={() => setDeleteTarget({ id: garden.id, name: garden.name })}
                        className="p-3 text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    {/* Swipeable garden row */}
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      disabled={switching !== null}
                      onClick={() => isRevealed ? setSwipedId(null) : handleSelect(garden)}
                      onTouchStart={(e) => handleTouchStart(garden.id, e)}
                      onTouchMove={(e) => handleTouchMove(garden.id, e)}
                      onTouchEnd={() => handleTouchEnd(garden.id)}
                      style={{
                        transform: `translateX(${translateX}px)`,
                        transition: activeTouchId === garden.id ? 'none' : 'transform 0.2s ease',
                      }}
                      className={`w-full flex items-center justify-between px-4 min-h-[56px] bg-white border-b border-gray-100 text-left disabled:cursor-wait ${
                        isActive ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <span className={`text-sm ${isActive ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {garden.name}
                      </span>
                      <span className="flex-shrink-0 ml-2">
                        {isLoading && <Loader2 size={16} className="text-emerald-600 animate-spin" />}
                        {isActive && !isLoading && <Check size={16} className="text-emerald-600" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            onClick={handleNewGarden}
            className="w-full flex items-center gap-2 px-4 min-h-[56px] text-sm text-emerald-600 hover:bg-gray-50 border-t border-gray-100"
          >
            <Plus size={16} />
            <span>Neuen Garten anlegen</span>
          </button>

          {/* Safe area bottom padding for iOS */}
          <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
        </div>
      </div>

      {deleteTarget && (
        <DeleteGardenDialog
          isOpen
          gardenName={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}
    </div>,
    document.body
  );
};

export default GardenSwitcherSheet;
